/**
 * O paciente no cabeçalho do Cliente 360 (#811, clínica): a idade, o
 * nascimento e o convênio principal.
 *
 * ── Por que no cabeçalho (16/10/2026) ────────────────────────────────────
 *
 * Quem atende a clínica pergunta "qual o convênio?" e "quantos anos?" antes
 * de qualquer outra coisa, e a resposta estava duas abas para dentro. No
 * cabeçalho ela fica à vista em todas as abas, como no plano "Clínica no
 * CliqCRM".
 *
 * ── Branco, e não roxo ───────────────────────────────────────────────────
 *
 * A pílula do convênio é neutra: o roxo é da ação (o botão primário, a aba
 * ativa), e uma pílula roxa ao lado de "Abrir conversa" disputaria o clique.
 *
 * ── Só o que já foi lido, sem ler de novo ────────────────────────────────
 *
 * O cabeçalho NÃO pede a ficha nem os convênios: só mostra o que a aba
 * "Cadastro" já leu (o cache é um só). Cada leitura da ficha registra no
 * registro de acesso todos os campos preenchidos — endereço, contato de
 * emergência —, e o cabeçalho, que aparece em todas as abas, faria isso em
 * toda abertura do 360 para mostrar só a idade e o convênio (revisão de LGPD,
 * 16/10/2026). Mostrar a idade e o convênio desde a primeira aba pede uma rota
 * própria do cabeçalho, que devolva só esses campos.
 *
 * A carteirinha aparece só com os quatro últimos, como em todo lugar.
 */

import type { ReactNode } from 'react';
import { useConveniosDoPaciente, useFichaDoPaciente } from '@/shared/api/consultas';
import { Icone } from '@/shared/ui';
import { tituloDoConvenio } from '../lib/convenio';
import { dataPorExtenso, hojeLocal, idadeEm } from '../lib/ficha-do-paciente';

export function PacienteNoCabecalho({ clienteId }: { clienteId: string }): ReactNode {
  // `false`: só o cache, nenhuma leitura própria (ver o cabeçalho do arquivo).
  const ficha = useFichaDoPaciente(clienteId, false);
  const convenios = useConveniosDoPaciente(clienteId, false);

  const nascimento = ficha.data?.nascimento ?? null;
  const lista = convenios.data?.itens ?? [];
  const principal = lista.find((convenio) => convenio.principal) ?? null;

  return (
    <>
      {nascimento && (
        <span>
          <b>{idadeEm(nascimento, hojeLocal())} anos</b> · nasc. {dataPorExtenso(nascimento)}
        </span>
      )}
      {principal && (
        <span className="c360__pilula-paciente" title="Convênio principal">
          <Icone nome="identidade" />
          {[
            tituloDoConvenio(principal),
            principal.carteirinha_final ? `•••• ${principal.carteirinha_final}` : null,
          ]
            .filter((parte): parte is string => parte !== null)
            .join(' · ')}
        </span>
      )}
      {lista.length > 1 && <span className="c360__pilula-paciente">{lista.length} convênios</span>}
    </>
  );
}
